import React from 'react';
import { createPortal } from 'react-dom';
import { buttonClass, theme } from '../../../ui/theme';

export default function SettingsPanel({
  showSettings,
  settingsPanelRef,
  settingsButtonRef,
  t,
  i18n,
  fadeInOut,
  setFadeInOut,
  playlistLoop,
  setPlaylistLoop,
  sleepTimer,
  sleepTimerMinutes,
  setSleepTimerHandler,
  selectedAudioOutputId,
  setSelectedAudioOutputId,
  setAudioOutputDevice,
  audioOutputDevices,
  enumerateAudioOutputDevices,
  showUiHint,
  isMobileDevice,
  audioFocusMode,
  setAudioFocusMode,
  lyricSettings,
  setLyricSettings
}) {
  // 根据语言获取分钟单位
  const getMinutesUnit = () => {
    const lang = i18n?.language || 'ja';
    if (lang === 'cn' || lang === 'tw') return '分';
    if (lang === 'en') return 'min';
    return t('player.minutes'); // 其他语言使用翻译
  };
  
  // 根据语言获取标签字体大小（日语环境使用更小字体）
  const getLabelFontSize = () => {
    const lang = i18n?.language || 'ja';
    return lang === 'ja' ? 'text-[10px]' : 'text-xs';
  };
  
  const minutesUnit = getMinutesUnit();
  const labelFontSize = getLabelFontSize();
  const [position, setPosition] = React.useState({ top: 0, left: 0, width: 0, height: 0 });
  const [audioDropdownOpen, setAudioDropdownOpen] = React.useState(false);
  const audioDropdownRef = React.useRef(null);
  const [translationDropdownOpen, setTranslationDropdownOpen] = React.useState({
    zh: false,
    ja: false,
    en: false,
    other: false
  });
  const translationDropdownRefZh = React.useRef(null);
  const translationDropdownRefJa = React.useRef(null);
  const translationDropdownRefEn = React.useRef(null);
  const translationDropdownRefOther = React.useRef(null);
  const translationDropdownRefs = React.useMemo(
    () => ({
      zh: translationDropdownRefZh,
      ja: translationDropdownRefJa,
      en: translationDropdownRefEn,
      other: translationDropdownRefOther
    }),
    []
  );

  // 点击外部关闭音频设备下拉菜单
  React.useEffect(() => {
    if (!audioDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (audioDropdownRef.current && !audioDropdownRef.current.contains(e.target)) {
        setAudioDropdownOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setAudioDropdownOpen(false);
      }
    };
    // 使用捕获阶段确保在其他事件处理器之前执行
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [audioDropdownOpen]);

  // 点击外部关闭翻译语言下拉菜单
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      const keys = ['zh', 'ja', 'en', 'other'];
      keys.forEach(key => {
        if (translationDropdownOpen[key]) {
          const ref = translationDropdownRefs[key].current;
          if (ref && !ref.contains(e.target)) {
            setTranslationDropdownOpen(prev => ({ ...prev, [key]: false }));
          }
        }
      });
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setTranslationDropdownOpen({ zh: false, ja: false, en: false, other: false });
      }
    };
    const hasOpen = Object.values(translationDropdownOpen).some(v => v);
    if (hasOpen) {
      // 使用捕获阶段确保在其他事件处理器之前执行
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [translationDropdownOpen, translationDropdownRefs]);

  // 计算设置按钮位置（使用节流避免频繁 DOM 读取）
  React.useEffect(() => {
    if (!showSettings || !settingsButtonRef?.current) return;

    let rafId = null;
    let pendingUpdate = false;

    const updatePosition = () => {
      if (pendingUpdate) return;
      pendingUpdate = true;

      rafId = requestAnimationFrame(() => {
        pendingUpdate = false;
        const button = settingsButtonRef.current;
        if (!button) return;

        const rect = button.getBoundingClientRect();
        setPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showSettings, settingsButtonRef]);

  if (!showSettings || typeof document === 'undefined') return null;

  // 计算面板位置：显示在按钮上方，确保不超出视口
  const panelWidth = Math.max(320, Math.min(528, window.innerWidth - 32));
  const estimatedPanelHeight = 400; // 估算面板高度
  const spaceAbove = position.top;
  const spaceBelow = window.innerHeight - position.top - position.height;
  
  // 如果上方空间不足，显示在下方；否则显示在上方
  const showAbove = spaceAbove >= estimatedPanelHeight || spaceAbove > spaceBelow;
  
  const panelStyle = showAbove
    ? {
        bottom: `${window.innerHeight - position.top + 8}px`,
        left: `${Math.max(16, Math.min(position.left, window.innerWidth - panelWidth - 16))}px`,
        width: `${panelWidth}px`,
        maxWidth: 'calc(100vw - 32px)'
      }
    : {
        top: `${position.top + position.height + 8}px`,
        left: `${Math.max(16, Math.min(position.left, window.innerWidth - panelWidth - 16))}px`,
        width: `${panelWidth}px`,
        maxWidth: 'calc(100vw - 32px)'
      };

  const panelContent = (
    <div
      ref={settingsPanelRef}
      className={[
        'fixed text-white p-3 mb-2 rounded-t-lg shadow-2xl max-h-[448px] overflow-y-auto overscroll-contain z-[10000]',
        theme.surfaces.modalPanel
      ].join(' ')}
      style={panelStyle}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      data-no-toggle
    >
      <div className="space-y-2 text-sm">
        <h3 className="font-semibold text-base mb-2">{t('player.settings')}</h3>

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.fadeInOut')}</div>
          <div className="flex-1 flex items-center justify-start">
            <input
              type="checkbox"
              checked={fadeInOut}
              onChange={(e) => setFadeInOut(e.target.checked)}
              className="h-4 w-4 accent-emerald-500"
            />
            <span className={`ml-2 ${labelFontSize} text-white/55`}>{t('player.fadeInOutDesc')}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.playlistLoop')}</div>
          <div className="flex-1 flex items-center justify-start">
            <input
              type="checkbox"
              checked={playlistLoop}
              onChange={(e) => setPlaylistLoop(e.target.checked)}
              className="h-4 w-4 accent-emerald-500"
            />
            <span className={`ml-2 ${labelFontSize} text-white/55`}>{t('player.playlistLoopDesc')}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.showTranslation')}</div>
          <div className="flex-1 flex items-center justify-start">
            <input
              type="checkbox"
              checked={lyricSettings?.showTranslation ?? true}
              onChange={(e) => {
                setLyricSettings(prev => ({
                  ...prev,
                  showTranslation: e.target.checked
                }));
              }}
              className="h-4 w-4 accent-emerald-500"
            />
          </div>
        </div>

        {lyricSettings?.showTranslation && (
          <div className="flex items-center gap-3">
            <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}></div>
            <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-2">
              {/* 中文歌词翻译目标语言 */}
              <div className="flex items-center gap-1">
                <div className={`${labelFontSize} font-medium w-[114px] flex-shrink-0`}>
                  {t('player.chineseTo') || '中文 →'}
                </div>
                <div className="flex-1 flex justify-start">
                  <div ref={translationDropdownRefs.zh} className="relative flex-1 min-w-0 -ml-12">
                    <button
                      type="button"
                      onClick={() => setTranslationDropdownOpen(prev => ({ ...prev, zh: !prev.zh }))}
                      className={[
                        'w-full h-[26px] rounded-md border border-white/10 text-white/90 px-1.5 text-[11px]',
                        'focus:ring-2 focus:ring-blue-400/25 focus:border-blue-400/30',
                        'bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435]',
                        'flex items-center justify-start truncate',
                        'transition-colors hover:bg-white/[0.05]'
                      ].join(' ')}
                    >
                      <span className="truncate">
                        {lyricSettings?.translationMap?.zh === 'ja' ? t('player.japanese') || '日语' :
                         lyricSettings?.translationMap?.zh === 'en' ? t('player.english') || '英语' :
                         t('player.japanese') || '日语'}
                      </span>
                    </button>
                    {translationDropdownOpen.zh && (
                      <div
                        className="absolute left-0 min-w-full w-auto bottom-full mb-1 rounded-lg border border-white/10 z-20 overflow-hidden bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435] shadow-[0_10px_25px_rgba(0,0,0,0.35)]"
                        role="listbox"
                      >
                        {[
                          { value: 'ja', label: t('player.japanese') || '日语' },
                          { value: 'en', label: t('player.english') || '英语' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={lyricSettings?.translationMap?.zh === opt.value}
                            onClick={() => {
                              setLyricSettings(prev => ({
                                ...prev,
                                translationMap: {
                                  ...prev.translationMap,
                                  zh: opt.value
                                }
                              }));
                              setTranslationDropdownOpen(prev => ({ ...prev, zh: false }));
                            }}
                            className={[
                              'w-full flex items-center justify-start text-left pl-3 pr-2 py-1 text-[11px] text-white/90 transition-colors whitespace-nowrap',
                              lyricSettings?.translationMap?.zh === opt.value
                                ? 'bg-white/[0.08]'
                                : 'bg-transparent hover:bg-white/[0.06]'
                            ].join(' ')}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 日语歌词翻译目标语言 */}
              <div className="flex items-center gap-1">
                <div className={`${labelFontSize} font-medium w-[114px] flex-shrink-0`}>
                  {t('player.japaneseTo') || '日语 →'}
                </div>
                <div className="flex-1 flex justify-start">
                  <div ref={translationDropdownRefs.ja} className="relative flex-1 min-w-0 -ml-12">
                    <button
                      type="button"
                      onClick={() => setTranslationDropdownOpen(prev => ({ ...prev, ja: !prev.ja }))}
                      className={[
                        'w-full h-[26px] rounded-md border border-white/10 text-white/90 px-1.5 text-[11px]',
                        'focus:ring-2 focus:ring-blue-400/25 focus:border-blue-400/30',
                        'bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435]',
                        'flex items-center justify-start truncate',
                        'transition-colors hover:bg-white/[0.05]'
                      ].join(' ')}
                    >
                      <span className="truncate">
                        {lyricSettings?.translationMap?.ja === 'zh' ? t('player.chinese') || '中文' :
                         lyricSettings?.translationMap?.ja === 'en' ? t('player.english') || '英语' :
                         t('player.chinese') || '中文'}
                      </span>
                    </button>
                    {translationDropdownOpen.ja && (
                      <div
                        className="absolute left-0 min-w-full w-auto bottom-full mb-1 rounded-lg border border-white/10 z-20 overflow-hidden bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435] shadow-[0_10px_25px_rgba(0,0,0,0.35)]"
                        role="listbox"
                      >
                        {[
                          { value: 'zh', label: t('player.chinese') || '中文' },
                          { value: 'en', label: t('player.english') || '英语' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={lyricSettings?.translationMap?.ja === opt.value}
                            onClick={() => {
                              setLyricSettings(prev => ({
                                ...prev,
                                translationMap: {
                                  ...prev.translationMap,
                                  ja: opt.value
                                }
                              }));
                              setTranslationDropdownOpen(prev => ({ ...prev, ja: false }));
                            }}
                            className={[
                              'w-full flex items-center justify-start text-left pl-3 pr-2 py-1 text-[11px] text-white/90 transition-colors whitespace-nowrap',
                              lyricSettings?.translationMap?.ja === opt.value
                                ? 'bg-white/[0.08]'
                                : 'bg-transparent hover:bg-white/[0.06]'
                            ].join(' ')}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 英语歌词翻译目标语言 */}
              <div className="flex items-center gap-1">
                <div className={`${labelFontSize} font-medium w-[114px] flex-shrink-0`}>
                  {t('player.englishTo') || '英语 →'}
                </div>
                <div className="flex-1 flex justify-start">
                  <div ref={translationDropdownRefs.en} className="relative flex-1 min-w-0 -ml-12">
                    <button
                      type="button"
                      onClick={() => setTranslationDropdownOpen(prev => ({ ...prev, en: !prev.en }))}
                      className={[
                        'w-full h-[26px] rounded-md border border-white/10 text-white/90 px-1.5 text-[11px]',
                        'focus:ring-2 focus:ring-blue-400/25 focus:border-blue-400/30',
                        'bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435]',
                        'flex items-center justify-start truncate',
                        'transition-colors hover:bg-white/[0.05]'
                      ].join(' ')}
                    >
                      <span className="truncate">
                        {lyricSettings?.translationMap?.en === 'zh' ? t('player.chinese') || '中文' :
                         lyricSettings?.translationMap?.en === 'ja' ? t('player.japanese') || '日语' :
                         t('player.chinese') || '中文'}
                      </span>
                    </button>
                    {translationDropdownOpen.en && (
                      <div
                        className="absolute left-0 min-w-full w-auto bottom-full mb-1 rounded-lg border border-white/10 z-20 overflow-hidden bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435] shadow-[0_10px_25px_rgba(0,0,0,0.35)]"
                        role="listbox"
                      >
                        {[
                          { value: 'zh', label: t('player.chinese') || '中文' },
                          { value: 'ja', label: t('player.japanese') || '日语' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={lyricSettings?.translationMap?.en === opt.value}
                            onClick={() => {
                              setLyricSettings(prev => ({
                                ...prev,
                                translationMap: {
                                  ...prev.translationMap,
                                  en: opt.value
                                }
                              }));
                              setTranslationDropdownOpen(prev => ({ ...prev, en: false }));
                            }}
                            className={[
                              'w-full flex items-center justify-start text-left pl-3 pr-2 py-1 text-[11px] text-white/90 transition-colors whitespace-nowrap',
                              lyricSettings?.translationMap?.en === opt.value
                                ? 'bg-white/[0.08]'
                                : 'bg-transparent hover:bg-white/[0.06]'
                            ].join(' ')}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 其他歌词翻译目标语言 */}
              <div className="flex items-center gap-1">
                <div className={`${labelFontSize} font-medium w-[114px] flex-shrink-0`}>
                  {t('player.otherTo') || '其他 →'}
                </div>
                <div className="flex-1 flex justify-start">
                  <div ref={translationDropdownRefs.other} className="relative flex-1 min-w-0 -ml-12">
                    <button
                      type="button"
                      onClick={() => setTranslationDropdownOpen(prev => ({ ...prev, other: !prev.other }))}
                      className={[
                        'w-full h-[26px] rounded-md border border-white/10 text-white/90 px-1.5 text-[11px]',
                        'focus:ring-2 focus:ring-blue-400/25 focus:border-blue-400/30',
                        'bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435]',
                        'flex items-center justify-start truncate',
                        'transition-colors hover:bg-white/[0.05]'
                      ].join(' ')}
                    >
                      <span className="truncate">
                        {lyricSettings?.translationMap?.other === 'zh' ? t('player.chinese') || '中文' :
                         lyricSettings?.translationMap?.other === 'ja' ? t('player.japanese') || '日语' :
                         lyricSettings?.translationMap?.other === 'en' ? t('player.english') || '英语' :
                         t('player.chinese') || '中文'}
                      </span>
                    </button>
                    {translationDropdownOpen.other && (
                      <div
                        className="absolute left-0 min-w-full w-auto bottom-full mb-1 rounded-lg border border-white/10 z-20 overflow-hidden bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435] shadow-[0_10px_25px_rgba(0,0,0,0.35)]"
                        role="listbox"
                      >
                        {[
                          { value: 'zh', label: t('player.chinese') || '中文' },
                          { value: 'ja', label: t('player.japanese') || '日语' },
                          { value: 'en', label: t('player.english') || '英语' }
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            role="option"
                            aria-selected={lyricSettings?.translationMap?.other === opt.value}
                            onClick={() => {
                              setLyricSettings(prev => ({
                                ...prev,
                                translationMap: {
                                  ...prev.translationMap,
                                  other: opt.value
                                }
                              }));
                              setTranslationDropdownOpen(prev => ({ ...prev, other: false }));
                            }}
                            className={[
                              'w-full flex items-center justify-start text-left pl-3 pr-2 py-1 text-[11px] text-white/90 transition-colors whitespace-nowrap',
                              lyricSettings?.translationMap?.other === opt.value
                                ? 'bg-white/[0.08]'
                                : 'bg-transparent hover:bg-white/[0.06]'
                            ].join(' ')}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 字幕样式设置 */}
        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.fontSize')}</div>
          <div className="flex-1 flex justify-start items-center gap-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="10"
                max="32"
                step="1"
                value={lyricSettings?.fontSize ?? 16}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 10 && value <= 32) {
                    setLyricSettings(prev => ({
                      ...prev,
                      fontSize: value
                    }));
                  }
                }}
                className={['text-xs px-2 py-1 w-20', theme.inputs.base].join(' ')}
              />
              <span className={`${labelFontSize} text-white/55`}>px</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>{t('player.glowIntensity')}</div>
          <div className="flex-1 flex justify-start">
            <div className="flex gap-1.5 flex-wrap justify-start">
              {['low', 'medium', 'high'].map(glow => (
                <button
                  key={glow}
                  onClick={() => {
                    setLyricSettings(prev => ({
                      ...prev,
                      highlightGlow: glow
                    }));
                  }}
                  className={buttonClass({
                    variant: lyricSettings?.highlightGlow === glow ? 'primary' : 'outline',
                    size: 'xs',
                    className: `text-[10px] px-1.5 py-0.5 h-5 leading-tight ${lyricSettings?.highlightGlow === glow ? '' : 'bg-white/[0.08] border-gray-400/30 text-white/70 hover:bg-white/[0.12] hover:border-gray-400/40'}`
                  })}
                >
                  {t(`player.glow${glow.charAt(0).toUpperCase() + glow.slice(1)}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0 flex items-center gap-2`}>
            {t('player.sleepTimer')}
            <span className={`${labelFontSize} text-white/55`}>
              ({sleepTimerMinutes > 0 ? `${sleepTimerMinutes} ${minutesUnit}` : t('player.notSet')})
            </span>
          </div>
          <div className="flex-1 flex justify-start">
            <div className="flex gap-0.5 flex-wrap justify-start">
              {[15, 30, 45, 60, 90, 120].map(minutes => (
                <button
                  key={minutes}
                  onClick={() => setSleepTimerHandler(minutes)}
                  className={buttonClass({
                    variant: sleepTimerMinutes === minutes ? 'primary' : 'outline',
                    size: 'xs',
                    className: `text-[10px] px-1 py-0.5 h-5 leading-tight ${sleepTimerMinutes === minutes ? '' : 'bg-white/[0.08] border-gray-400/30 text-white/70 hover:bg-white/[0.12] hover:border-gray-400/40'}`
                  })}
                >
                  {minutes}{minutesUnit}
                </button>
              ))}
              {sleepTimer && (
                <button
                  onClick={() => setSleepTimerHandler(0)}
                  className={buttonClass({ 
                    variant: 'danger', 
                    size: 'xs',
                    className: 'text-[10px] px-1 py-0.5 h-5 leading-tight'
                  })}
                >
                  取消
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0 pt-1`}>
            {t('player.audioOutputDevice')}
            {isMobileDevice() && (
              <div className={`${labelFontSize} text-white/55 mt-0.5`}>{t('player.mobileRestriction')}</div>
            )}
          </div>
          <div className="flex-1 flex justify-start items-center gap-2 flex-nowrap">
            <div ref={audioDropdownRef} className="relative flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setAudioDropdownOpen(!audioDropdownOpen)}
                disabled={isMobileDevice() && audioOutputDevices.length <= 1}
                className={[
                  'w-full h-[26px] rounded-md border border-white/10 text-white/90 px-1.5 text-[11px]',
                  'focus:ring-2 focus:ring-blue-400/25 focus:border-blue-400/30',
                  'bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435]',
                  'flex items-center justify-center truncate',
                  'disabled:opacity-40 disabled:cursor-not-allowed',
                  'transition-colors hover:bg-white/[0.05]'
                ].join(' ')}
                title={
                  isMobileDevice()
                    ? t('player.mobileAudioOutputNote')
                    : t('player.selectAudioOutputDevice')
                }
              >
                <span className="truncate">
                  {audioOutputDevices.find(d => d.deviceId === selectedAudioOutputId)?.label || 
                   audioOutputDevices[0]?.label || 
                   t('player.defaultDevice')}
                </span>
              </button>
              {audioDropdownOpen && (
                <div
                  className="absolute left-0 w-full bottom-full mb-1 rounded-lg border border-white/10 z-20 overflow-hidden bg-gradient-to-r from-[#1b2435] via-[#222d41] to-[#1b2435] shadow-[0_10px_25px_rgba(0,0,0,0.35)] max-h-48 overflow-y-auto"
                  role="listbox"
                >
                  {audioOutputDevices.map((device, index) => (
                    <button
                      key={`${device.deviceId}-${index}`}
                      type="button"
                      role="option"
                      aria-selected={device.deviceId === selectedAudioOutputId}
                      onClick={() => {
                        setSelectedAudioOutputId(device.deviceId);
                        setAudioOutputDevice(device.deviceId);
                        setAudioDropdownOpen(false);
                      }}
                      className={[
                        'w-full text-left pl-6 pr-2 py-1 text-[11px] text-white/90 transition-colors',
                        device.deviceId === selectedAudioOutputId
                          ? 'bg-white/[0.08]'
                          : 'bg-transparent hover:bg-white/[0.06]'
                      ].join(' ')}
                    >
                      {device.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={async () => {
                await enumerateAudioOutputDevices();
                showUiHint(t('hints.audioOutputListRefreshed'), { duration: 1500 });
              }}
              className={buttonClass({ variant: 'outline', size: 'sm', className: 'flex-shrink-0 whitespace-nowrap relative z-30 bg-white/[0.08] border-gray-400/30 text-white/70 hover:bg-white/[0.12] hover:border-gray-400/40' })}
              title={t('player.refreshDeviceList')}
              disabled={isMobileDevice() && audioOutputDevices.length <= 1}
            >
              {t('player.refresh')}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`${labelFontSize} font-medium w-[146px] flex-shrink-0`}>
            {t('player.audioFocusMode')}
            <div className="text-xs text-white/55 mt-0.5">
              {audioFocusMode === 'exclusive' ? t('player.exclusivePriority') : t('player.sharedNormal')}
            </div>
          </div>
          <div className="flex-1 flex justify-start">
            <div className="flex gap-2 flex-wrap justify-start">
              <button
                onClick={() => setAudioFocusMode('shared')}
                className={buttonClass({
                  variant: audioFocusMode === 'shared' ? 'primary' : 'outline',
                  size: 'xs',
                  className: audioFocusMode === 'shared' ? '' : 'bg-white/[0.08] border-gray-400/30 text-white/70 hover:bg-white/[0.12] hover:border-gray-400/40'
                })}
              >
                {t('player.sharedMode')}
              </button>
              <button
                onClick={() => setAudioFocusMode('exclusive')}
                className={buttonClass({
                  variant: audioFocusMode === 'exclusive' ? 'primary' : 'outline',
                  size: 'xs',
                  className: audioFocusMode === 'exclusive' ? '' : 'bg-white/[0.08] border-gray-400/30 text-white/70 hover:bg-white/[0.12] hover:border-gray-400/40'
                })}
              >
                {t('player.exclusiveMode')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(panelContent, document.body);
}

